import { computed, Ref } from 'vue'
import { MaybeRef } from '@vueuse/core'
import { useTimers } from '../store/timers'
import { useStoreProfiles } from '../store/useStoreProfiles'
import md5 from 'md5'
import { unref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { addBreadcrumb, captureException } from '@sentry/vue'
import { useStringToDate } from '../composables/stringToDate'
import { Nullish, NullishString } from '../types/utility'
import CryptoJS from 'crypto-js'

export const sleepAsync = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const hashNoStringify = (data: MaybeRef<any>) => {
  return md5(unref(data))
}

export const hash = (data: MaybeRef<any>) => {
  return md5(JSON.stringify(unref(data)))
}

export const useProgressiveHash = async (data: any) => {
  const pHash = progressiveHash()
  pHash.update('[')
  for (let i = 0; i < data.length; i++) {
    await updatePHash(pHash, data[i], i < data.length - 1)
    if (i % 25 == 0) {
      await yieldToMain()
    }
  }
  pHash.update(']')
  return pHash.get()
}

async function updatePHash(pHash: any, toHash: any, notLast: boolean) {
  if (toHash) {
    pHash.update(JSON.stringify(toHash))
  }
  if (notLast) {
    pHash.update(',')
  }
}

function yieldToMain() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0)
  })
}

export const progressiveHash = () => {
  const h = CryptoJS.algo.MD5.create()

  return {
    update: (data: any) => {
      h.update(data)
    },
    get: () => h.finalize().toString(CryptoJS.enc.Hex),
  }
}

export const isNative = computed(() => Capacitor.isNativePlatform())

export function getCurrentStore(): any {
  const { getActiveParentTimer } = useTimers()
  const { getStore } = useStoreProfiles()
  return getStore(getActiveParentTimer?.data.store_id)
}

/** helper to remove trailing seconds/milliseconds, sets ref to date provided */
export const stripTime = (ref: Ref<Date | any>, date: Date) => {
  if (ref.value instanceof Date) {
    ref.value.setFullYear(date.getFullYear())
    ref.value.setMonth(date.getMonth())
    ref.value.setDate(date.getDate())
    ref.value.setSeconds(0)
    ref.value.setMilliseconds(0)
  }
}

export function isValidDateObject(val: any): val is Date {
  if (isNullish(val)) return false
  if (!isNaN(val) && Object.prototype.toString.call(val) === '[object Date]') return true
  return false
}

/**
 * Checks if a given value is a date or could be turned into one
 *
 * @param val some value
 *
 * @returns true if the value is a valid date, otherwise false
 */
export function isValidDate(val: any): val is Date | string {
  if (isNullish(val)) return false
  if (!isNaN(val) && Object.prototype.toString.call(val) === '[object Date]') return true
  //seehttps://stackoverflow.com/questions/643782/how-to-check-whether-an-object-is-a-date
  if (val.length == 0) return false
  // From here, make absolutely sure the value is a string so we can split on it
  if (!(typeof val === 'string' || val instanceof String)) return false
  const checkDate = new Date()
  const [hours, minutes] = val.split(':')
  checkDate.setHours(parseInt(hours))
  checkDate.setMinutes(parseInt(minutes))
  // @ts-expect-error isNaN will return true if `checkDate` is an `Invalid Date`, but TS freaks out if a number isn't passed in for some reason
  return !isNaN(checkDate) && Object.prototype.toString.call(checkDate) === '[object Date]'
}

/**
 * Turns a given value into a date object
 *
 * @param val a valid date-like value (validated by `isValidDate` beforehand)
 * @param date (optional) date object to gather the current year/month/day information
 *
 * @returns the parsed date object, or null if the given value was not a valid date
 */
export function toDate(val: any, date?: Date): Date | null {
  if (isValidDate(val)) {
    const baseDate = date ?? new Date()
    return useStringToDate(val, { date: baseDate })
  }
  return null
}

export function logInfo(
  level: 'error' | 'debug',
  category: string,
  message: string,
  data: any
): void {
  addBreadcrumb({
    category,
    message,
    level,
    data,
  })
  if (level === 'error') {
    console.error(message)
    captureException(category)
  } else {
    console.log(message)
  }
}

export function parseBool(value: any) {
  if (
    value === true ||
    String(value).toUpperCase() === 'TRUE' ||
    String(value).toUpperCase() === 'T' ||
    String(value).toUpperCase() === 'YES' ||
    String(value).toUpperCase() === '1'
  ) {
    return true
  }
  return false
}

/** Various utilities and type guards for null or null-like values (null, undefined, 'null', and 'undefined') */
export const NullishValues: Nullish[] = [null, undefined]
export const NullishStringValues: NullishString[] = [null, undefined, 'null', 'undefined']
export function isNullish(value: any): value is Nullish {
  return NullishValues.includes(value)
}
export function isNullishString(value: any): value is NullishString {
  return NullishStringValues.includes(value)
}

/**
 * Helper function to determine if an object property (can be deeply nested) is Nullish.
 *
 * The second argument can either be a string key, or an array of string keys leading to the property you wish to check.
 * (e.g. passing in `['a', 'b', 'c']` will check if value.a.b.c is Nullish. If any key along the way is nullish, it will return true)
 *
 * If the value itself is Nullish, this will return true.
 * If the value passed in is not a proper JS object (cannot be an array or function) this will also return true.
 *
 * @param value object value to check if a property on it is nullish
 * @param keys the property key (or the traversal path to the key) to check if it is Nullish
 * @returns true if the value,
 */
export function isPropertyOfObjectNullish(value: any, keys: string | string[]): boolean {
  if (isNullish(value)) {
    return true
  }
  if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Function)) {
    // The thing we're checking is actually an object, so we can start traversing it
    if (Array.isArray(keys)) {
      // We have an array of keys, so start traversing the rest of it
      const keysCopy = [...keys]
      const currentKey = keysCopy.shift()
      if (isNullish(currentKey)) {
        return true
      }
      const valueAtKey = value[currentKey]
      if (isNullish(valueAtKey)) {
        return true
      }
      if (keysCopy && keysCopy.length) {
        return isPropertyOfObjectNullish(valueAtKey, keysCopy)
      }
      return false
    } else {
      return isNullish(value[keys])
    }
  }
  return true
}

export function stripDataTag(data: string): string {
  const hasPrefix = /^data:image\/.*;base64,/.test(data)
  if (hasPrefix) {
    return data.replace(/^data:image\/.*;base64,/, '')
  }
  return data
}
